import type { AnalysisRunStatusResponse, AnalysisRunTrigger } from "@/types/analysis";

/**
 * What a person needs to know about the Reality Model, derived from the
 * analysis-run status. The wire speaks in passes; this speaks in steps with
 * plain names. The server reports `totalPasses: 3` while running up to six
 * passes, so the step count here comes from the pass number, never from it.
 */
export type RealityModelPhase = "never" | "fresh" | "queued" | "updating" | "failed" | "stopped";

export interface RealityModelStep {
  /** 1-based. */
  index: number;
  total: number;
  label: string;
  /** 0..1 within this step, or null when the step reports no item total. */
  fraction: number | null;
}

export interface RealityModelState {
  phase: RealityModelPhase;
  step: RealityModelStep | null;
  /** When the shown run finished (fresh, failed, stopped). */
  finishedAt: string | null;
  startedAt: string | null;
  startedBy: string | null;
  files: number | null;
  symbols: number | null;
  errorMessage: string | null;
}

export const REALITY_MODEL_STEPS = [
  "Reading your code",
  "Finding how code connects",
  "Linking docs to code",
  "Mapping services and data",
  "Mapping governance",
  "Reading library docs",
] as const;

const TRIGGER_LABEL: Record<AnalysisRunTrigger, string> = {
  Manual: "Started by hand",
  FileWrite: "Started by a code change",
  Checkout: "Started by new commits",
  Startup: "Started when the workspace opened",
};

export function deriveRealityModelState(status: AnalysisRunStatusResponse | undefined): RealityModelState {
  const run = status?.run ?? null;
  const base: RealityModelState = {
    phase: "never",
    step: null,
    finishedAt: run?.completedAt ?? null,
    startedAt: run?.startedAt ?? run?.createdAt ?? null,
    startedBy: run ? TRIGGER_LABEL[run.trigger] ?? null : null,
    files: run?.summary?.fileCount ?? null,
    symbols: run?.summary?.symbolCount ?? null,
    errorMessage: run?.errorMessage ?? null,
  };

  if (!status || !run || status.status === "Idle") {
    return base;
  }

  switch (status.status) {
    case "Completed":
      return { ...base, phase: "fresh" };
    case "Failed":
      return { ...base, phase: "failed", step: stepOf(run) };
    case "Cancelled":
      return { ...base, phase: "stopped" };
    case "Pending":
      return { ...base, phase: "queued" };
    case "Running":
      return { ...base, phase: "updating", step: stepOf(run) };
    default:
      return base;
  }
}

function stepOf(run: NonNullable<AnalysisRunStatusResponse["run"]>): RealityModelStep {
  const total = REALITY_MODEL_STEPS.length;
  const index = Math.min(Math.max(run.currentPass || 1, 1), total);
  const fraction = run.passTotal && run.passTotal > 0
    ? Math.min(1, Math.max(0, run.passProcessed / run.passTotal))
    : null;
  return { index, total, label: REALITY_MODEL_STEPS[index - 1], fraction };
}

/** Short state word for the top-bar chip. */
export function realityModelChipLabel(state: RealityModelState): string {
  switch (state.phase) {
    case "fresh":
      return "Up to date";
    case "updating":
      return "Updating";
    case "queued":
      return "Starting";
    case "failed":
      return "Update failed";
    case "stopped":
      return "Update stopped";
    default:
      return "Not built yet";
  }
}

export function formatAgo(value: string | null, now: number = Date.now()): string | null {
  if (!value) return null;
  const time = Date.parse(value);
  if (Number.isNaN(time)) return null;
  const seconds = Math.max(0, (now - time) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3_600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3_600)} h ago`;
  const days = Math.round(seconds / 86_400);
  if (days < 14) return `${days} d ago`;
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date(time));
}
